import * as styles from "./AmendmentEmployeesTab.css";

import type { JSX } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import { Trans } from "@lingui/react/macro";

import type { AmendmentBatchResult } from "../types";

export type AmendmentBatchReportProps = {
  result: AmendmentBatchResult | null;
  /** Shown when the report is a preview the reader still has to confirm. */
  confirmLabel?: string;
  onConfirm?: () => void;
  onClose: () => void;
};

/**
 * What a batch did, or would do. A batch has no single outcome, so it reports
 * rather than raising: the rows it could not serve are named.
 */
export function AmendmentBatchReport({
  result,
  confirmLabel,
  onConfirm,
  onClose,
}: AmendmentBatchReportProps): JSX.Element | null {
  if (null === result) {
    return null;
  }

  // Interpolating an expression rather than a name breaks the translation: the
  // React compiler hoists it into a counter-named temporary, and the
  // placeholder Lingui reads at build time no longer matches the extracted one.
  const { succeeded, skipped, failed } = result;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>
            <Trans>Batch report</Trans>
          </DialogTitle>

          <DialogClose />
        </DialogHeader>

        <DialogBody>
          <p>
            <Trans>
              {succeeded} row(s) served, {skipped} skipped, {failed} refused.
            </Trans>
          </p>

          {result.issues.length > 0 ? (
            <ul className={styles.issues}>
              {result.issues.map((issue) => (
                <li key={`${issue.employeeId}-${issue.reason}`}>
                  {issue.employeeName} — {issue.reason}
                </li>
              ))}
            </ul>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button variant="neutral" onClick={onClose}>
            <Trans>Close</Trans>
          </Button>

          {undefined === onConfirm ? null : (
            <Button variant="primary" onClick={onConfirm}>
              {confirmLabel}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
